'use client';

import Image from 'next/image';
import { X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  consumeAuthTokenFromUrl,
  loginWithTelegramWidget,
  loginWithVk,
  loginWithYandexToken,
  setStoredToken,
} from '@/lib/auth-client';
import { captureReferralFromUrl } from '@/lib/referral';

type AuthDialogContextValue = {
  open: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  imageUrl: string | null;
};

const AuthDialogContext = createContext<AuthDialogContextValue | null>(null);

export function useAuthDialog() {
  const ctx = useContext(AuthDialogContext);
  if (!ctx) {
    throw new Error('useAuthDialog must be used inside AuthDialogProvider');
  }
  return ctx;
}

export function AuthDialogProvider({
  children,
  authModalImage,
}: {
  children: ReactNode;
  authModalImage?: string | null;
}) {
  const [open, setOpen] = useState(false);
  const openDialog = useCallback(() => setOpen(true), []);
  const closeDialog = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeDialog();
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, closeDialog]);

  // Yandex flow lands the browser on "/#auth_token=<jwt>" — consume it into localStorage.
  // Sibling concern: capture `?ref=<code>` from the URL so a referral
  // link survives the OAuth round-trip (which would otherwise strip
  // the query string before we ever ask for the access token).
  useEffect(() => {
    consumeAuthTokenFromUrl();
    captureReferralFromUrl();
  }, []);

  return (
    <AuthDialogContext.Provider
      value={{
        open,
        openDialog,
        closeDialog,
        imageUrl: authModalImage ?? null,
      }}
    >
      {children}
      <AuthDialogModal />
    </AuthDialogContext.Provider>
  );
}

function AuthDialogModal() {
  const { open, closeDialog, imageUrl } = useAuthDialog();

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal
      aria-labelledby="auth-dialog-title"
      onClick={closeDialog}
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm"
    >
      <div
        onClick={(event) => event.stopPropagation()}
        className="relative flex w-full max-w-[900px] overflow-hidden rounded-2xl shadow-[0_25px_44px_-12px_rgba(0,0,0,0.4)]"
      >
        <div className="relative flex w-full flex-col justify-center bg-[#111111] px-6 py-12 sm:px-8 md:w-1/2 md:px-10 md:py-14">
          <h2
            id="auth-dialog-title"
            className="max-w-[320px] self-center text-center font-display text-2xl font-bold leading-[1.2] text-white"
          >
            Зарегистрироваться или Войти и создавать
          </h2>

          <form
            className="mt-[25px] flex flex-col gap-[25px]"
            onSubmit={(event) => {
              event.preventDefault();
            }}
          >
            <div className="flex flex-col gap-[10px]">
              <OAuthButton
                provider="google"
                icon={<GoogleIcon />}
                label="Войти через Google"
                onClick={() => {
                  // Server-side redirect flow — `next=/app` brings the
                  // user straight into the workspace once the JWT lands
                  // back in the URL fragment.
                  const apiBase = process.env.NEXT_PUBLIC_API_URL ?? '';
                  window.location.href = `${apiBase}/api/auth/google?next=/app`;
                }}
              />
              <YandexSuggestMount />
              <VkOneTapMount />
              <TelegramLoginMount />
            </div>

            <p className="text-center text-xs leading-[1.33] text-[#6a7282]">
              Продолжая, вы соглашаетесь с{' '}
              <a href="/terms" className="underline transition-colors hover:text-white/80">
                Условиями использования
              </a>{' '}
              и{' '}
              <a href="/privacy" className="underline transition-colors hover:text-white/80">
                Политикой конфиденциальности
              </a>
              .
            </p>
          </form>
        </div>

        <div className="relative hidden bg-[#f3f4f6] md:block md:aspect-[2/3] md:w-1/2">
          <Image
            src={imageUrl ?? '/idea-mock-1.svg'}
            alt=""
            fill
            unoptimized={imageUrl ? imageUrl.startsWith('http') : false}
            sizes="(min-width: 768px) 450px, 0px"
            className="object-cover"
          />
        </div>

        <button
          type="button"
          aria-label="Закрыть"
          onClick={closeDialog}
          className="absolute right-4 top-[21px] inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-700/60 text-white transition-colors hover:bg-slate-700/80"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
}

type OAuthProvider = 'google' | 'yandex' | 'vk' | 'telegram';

const OAUTH_STYLES: Record<OAuthProvider, string> = {
  google: 'bg-white text-black hover:bg-white/90',
  // Modern Yandex ID brand button — dark base, red Яндекс ID mark, white text.
  yandex: 'bg-[#000000] text-white hover:bg-[#1a1a1a]',
  vk: 'bg-[#0077ff] text-white hover:bg-[#0068e0]',
  telegram: 'bg-[#26a5e4] text-white hover:bg-[#1e91cc]',
};

function OAuthButton({
  provider,
  icon,
  label,
  onClick,
  disabled,
}: {
  provider: OAuthProvider;
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-[52px] w-full items-center justify-center gap-3 rounded-full text-base font-medium tracking-tight transition-colors ${OAUTH_STYLES[provider]} disabled:cursor-not-allowed disabled:opacity-60`}
    >
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center">
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden>
      <path
        fill="#EA4335"
        d="M9 3.48c1.69 0 2.84.73 3.49 1.34l2.54-2.48C13.46 1.14 11.42 0 9 0 5.48 0 2.44 2.02.96 4.96l2.96 2.3C4.68 4.78 6.67 3.48 9 3.48Z"
      />
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.05-1.25-.15-1.84H9v3.48h4.84c-.21 1.13-.84 2.09-1.79 2.73l2.87 2.23c1.68-1.55 2.72-3.83 2.72-6.6Z"
      />
      <path
        fill="#FBBC05"
        d="M3.92 10.71A5.4 5.4 0 0 1 3.63 9c0-.6.1-1.18.28-1.71L.96 4.99A8.96 8.96 0 0 0 0 9c0 1.45.35 2.82.96 4.01l2.96-2.3Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.87-2.23c-.8.54-1.88.92-3.09.92-2.33 0-4.32-1.3-5.04-3.62L1.01 13.2C2.49 16.13 5.52 18 9 18Z"
      />
    </svg>
  );
}

interface VKWidget {
  on: (event: unknown, cb: (payload: unknown) => void) => VKWidget;
}
interface VKOneTap {
  render: (opts: {
    container: HTMLElement;
    showAlternativeLogin?: boolean;
    styles?: { height?: number; borderRadius?: number; width?: number | string };
  }) => VKWidget;
}
type VKIDModule = {
  Config: { init: (config: Record<string, unknown>) => void };
  OneTap: new () => VKOneTap;
  ConfigResponseMode: { Callback: unknown };
  ConfigSource: { LOWCODE: unknown };
  Auth: {
    exchangeCode: (
      code: string,
      deviceId: string,
    ) => Promise<{ access_token: string; user_id: number; email?: string }>;
  };
  WidgetEvents: { ERROR: unknown };
  OneTapInternalEvents: { LOGIN_SUCCESS: unknown };
};

interface YaAuthSuggestResult {
  access_token?: string;
  accessToken?: string;
  state?: string;
}
interface YaAuthSuggestModule {
  init: (
    oauthQueryParams: {
      client_id: string;
      response_type: string;
      redirect_uri: string;
    },
    tokenPageOrigin: string,
    suggestParams?: {
      view?: 'button';
      parentId?: string;
      buttonView?: 'main' | 'additional' | 'icon' | 'iconBG';
      buttonTheme?: 'light' | 'dark';
      buttonSize?: 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl';
      buttonBorderRadius?: number;
    },
  ) => Promise<{ handler: () => Promise<YaAuthSuggestResult> }>;
}

declare global {
  interface Window {
    VKIDSDK?: VKIDModule;
    YaAuthSuggest?: YaAuthSuggestModule;
  }
}

/**
 * Mount-point for VK ID OneTap widget. Lazy-loads the SDK once and, once ready,
 * renders VK's own branded button into the dialog. On success, exchanges the
 * returned code for an access_token client-side (VK ID PKCE flow) and hands it
 * to our backend for verification via secure.checkToken.
 */
function VkOneTapMount() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.VKIDSDK) {
      setReady(true);
      return;
    }
    const existing = document.getElementById('vkid-sdk') as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.id = 'vkid-sdk';
      script.src = 'https://unpkg.com/@vkid/sdk@<3.0.0/dist-sdk/umd/index.js';
      script.async = true;
      document.head.appendChild(script);
    }
    const onLoad = () => setReady(Boolean(window.VKIDSDK));
    script.addEventListener('load', onLoad);
    return () => script.removeEventListener('load', onLoad);
  }, []);

  useEffect(() => {
    if (!ready || !containerRef.current) return;
    const VKID = window.VKIDSDK;
    const appId = process.env.NEXT_PUBLIC_VK_APP_ID;
    if (!VKID || !appId) return;

    VKID.Config.init({
      app: Number(appId),
      redirectUrl: `${window.location.origin}/api/auth/callback/vk`,
      responseMode: VKID.ConfigResponseMode.Callback,
      source: VKID.ConfigSource.LOWCODE,
      scope: 'email',
    });

    const oneTap = new VKID.OneTap();
    oneTap
      .render({
        container: containerRef.current,
        showAlternativeLogin: false,
        // Match the surrounding OAuth row: 52px tall pill, full-width.
        styles: { height: 52, borderRadius: 26, width: '100%' },
      })
      .on(VKID.WidgetEvents.ERROR, (error: unknown) => {
        console.error('[VK] widget error', error);
      })
      .on(VKID.OneTapInternalEvents.LOGIN_SUCCESS, (payload: unknown) => {
        const { code, device_id } = payload as { code: string; device_id: string };
        VKID.Auth.exchangeCode(code, device_id)
          .then(async (data) => {
            const result = await loginWithVk({
              access_token: data.access_token,
              user_id: data.user_id,
              email: data.email,
            });
            if (result?.token) {
              setStoredToken(result.token);
              window.location.href = '/app';
            }
          })
          .catch((error) => console.error('[VK] exchange failed', error));
      });
  }, [ready]);

  return <div ref={containerRef} className="flex h-[52px] w-full items-center justify-center [&>*]:w-full" />;
}

/**
 * Renders the official Yandex ID SDK button (via YaAuthSuggest in view:button
 * mode). The SDK shows the logged-in Yandex user's name/avatar inside the
 * button when available and handles the whole popup → token → postMessage
 * flow itself. We just take the access_token the SDK hands us and verify it
 * on our backend.
 *
 * Relies on helmet() having `crossOriginOpenerPolicy: false` so window.opener
 * survives the popup's hop through oauth.yandex.ru.
 */
const YANDEX_CONTAINER_ID = 'ya-auth-suggest-button';

function YandexSuggestMount() {
  const [sdkReady, setSdkReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.YaAuthSuggest) {
      setSdkReady(true);
      return;
    }
    const existing = document.getElementById('yandex-suggest-sdk') as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.id = 'yandex-suggest-sdk';
      script.src =
        'https://yastatic.net/s3/passport-sdk/autofill/v1/sdk-suggest-with-polyfills-latest.js';
      script.async = true;
      document.head.appendChild(script);
    }
    const onLoad = () => setSdkReady(Boolean(window.YaAuthSuggest));
    script.addEventListener('load', onLoad);
    return () => script.removeEventListener('load', onLoad);
  }, []);

  useEffect(() => {
    if (!sdkReady || !window.YaAuthSuggest) return;
    const clientId = process.env.NEXT_PUBLIC_YANDEX_CLIENT_ID;
    if (!clientId) return;

    let cancelled = false;
    window.YaAuthSuggest.init(
      {
        client_id: clientId,
        response_type: 'token',
        redirect_uri: `${window.location.origin}/api/auth/callback/yandex`,
      },
      window.location.origin,
      {
        view: 'button',
        parentId: YANDEX_CONTAINER_ID,
        buttonView: 'main',
        buttonTheme: 'dark',
        buttonSize: 'l',
        buttonBorderRadius: 26,
      },
    )
      .then(({ handler }) => handler())
      .then(async (data) => {
        if (cancelled) return;
        const accessToken = data.access_token ?? data.accessToken;
        if (!accessToken) return;
        const result = await loginWithYandexToken(accessToken);
        if (result?.token) {
          setStoredToken(result.token);
          window.location.href = '/app';
        }
      })
      .catch((error) => console.error('[Yandex] widget error', error));

    return () => {
      cancelled = true;
    };
  }, [sdkReady]);

  return (
    <div
      id={YANDEX_CONTAINER_ID}
      className="flex h-[52px] w-full items-center justify-center [&>*]:w-full"
    />
  );
}

interface TelegramWidgetUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  photo_url?: string;
  auth_date: number;
  hash: string;
}

function TelegramIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#fff"
        d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0Zm5.568 8.16c-.18 1.897-.96 6.502-1.36 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.46-1.901-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.213-.07-.062-.173-.04-.247-.024-.106.024-1.793 1.139-5.061 3.345-.479.329-.913.49-1.302.482-.428-.008-1.252-.241-1.865-.44-.751-.244-1.349-.374-1.297-.788.027-.215.328-.435.901-.66 3.527-1.537 5.879-2.55 7.057-3.04 3.36-1.397 4.058-1.64 4.513-1.648.1-.001.323.023.467.14.123.099.157.232.173.327.016.094.036.31.02.477Z"
      />
    </svg>
  );
}

interface TelegramGlobal {
  Login?: {
    auth: (
      options: { bot_id: string; request_access?: 'write'; lang?: string },
      callback: (data: TelegramWidgetUser | false) => void,
    ) => void;
  };
}

/**
 * Custom Telegram-login pill — replaces the official `telegram-widget.js`
 * iframe (which renders a fixed-width 186px box, tiny next to the other
 * 52px pills). We still load Telegram's own widget script so we can call
 * its documented `Telegram.Login.auth()` helper — same code path the
 * iframe button uses internally, so popup, postMessage and COOP quirks
 * are handled by Telegram, not by us. We just receive the signed payload
 * via the callback and hand it to the existing
 * /api/auth/telegram/web endpoint.
 */
function TelegramLoginMount() {
  const [scriptReady, setScriptReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const tg = (window as unknown as { Telegram?: TelegramGlobal }).Telegram;
    if (tg?.Login?.auth) {
      setScriptReady(true);
      return;
    }
    const existing = document.getElementById('telegram-widget-sdk') as HTMLScriptElement | null;
    const script = existing ?? document.createElement('script');
    if (!existing) {
      script.id = 'telegram-widget-sdk';
      script.src = 'https://telegram.org/js/telegram-widget.js?22';
      script.async = true;
      document.head.appendChild(script);
    }
    const onLoad = () => {
      const ready = (window as unknown as { Telegram?: TelegramGlobal }).Telegram?.Login?.auth;
      setScriptReady(Boolean(ready));
    };
    script.addEventListener('load', onLoad);
    return () => script.removeEventListener('load', onLoad);
  }, []);

  const handleClick = useCallback(() => {
    const botId = process.env.NEXT_PUBLIC_TELEGRAM_BOT_ID;
    if (!botId) {
      console.error('[telegram-auth] NEXT_PUBLIC_TELEGRAM_BOT_ID missing');
      return;
    }
    const tg = (window as unknown as { Telegram?: TelegramGlobal }).Telegram;
    if (!tg?.Login?.auth) {
      console.error('[telegram-auth] telegram-widget.js not ready');
      return;
    }

    tg.Login.auth({ bot_id: botId, request_access: 'write' }, async (data) => {
      // Telegram passes `false` if the user closed the popup.
      if (!data) return;
      try {
        const result = await loginWithTelegramWidget(
          data as unknown as Record<string, unknown>,
        );
        if (result?.token) {
          setStoredToken(result.token);
          window.location.href = '/app';
        }
      } catch (err) {
        console.error('[telegram-auth] backend exchange failed', err);
      }
    });
  }, []);

  return (
    <OAuthButton
      provider="telegram"
      icon={<TelegramIcon />}
      label="Войти через Telegram"
      onClick={handleClick}
      disabled={!scriptReady}
    />
  );
}


